import { Alert, Box } from "@mui/material";
import { chain } from "lodash-es";
import { ReactNode, useMemo, useState } from "react";

import AppButton from "@/common/components/Button/AppButton";
import AppIconButton, { AppIconButtonProps } from "@/common/components/Button/AppIconButton";
import VehicleVisualModelLink from "@/common/components/Entity/VehicleVisualModel/VehicleVisualModelLink";
import { VehicleVisualModelViewModal } from "@/common/components/Entity/VehicleVisualModel/View/VehicleVisualModelViewModal";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import { VisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { SvgHelper } from "@/common/helpers/svg";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import {
  VisualModelImageSelector,
  VisualModelNewPointClickedParams,
  VisualModelPoint,
  VisualModelPointClickedParams,
  VisualModelSvgPointMovedParams,
} from "@/common/ts/visualModel";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleVisualModelDto } from "@/core/api/generated/v1";

import { GenericSvgViewer, GenericSvgViewerProps } from "../../../../Images/Svg/GenericSvgViewer";

const controlIconButtonProps: AppIconButtonProps = {
  variant: "text",
  size: "small",
  color: "default",
};

export interface VisualModelSvgViewerProps<TPoint extends VisualModelPoint = VisualModelPoint> {
  visualModelId: Nil<string>;
  visualModel: Nil<VehicleVisualModelDto>;
  /** Main visual model image selector. */
  imageSelector: VisualModelImageSelector;
  /** Fallback selector if image is not found by the main selector. */
  fallbackImageSelector?: VisualModelImageSelector;
  points: TPoint[];
  isFetchVisualModel?: boolean;
  withVisualModelInfo?: boolean;
  debugKey?: string;
  svgViewerProps: Partial<Omit<GenericSvgViewerProps, "svgUrl" | "points">>;
  renderCustomControls?: (params: { iconButtonProps: AppIconButtonProps }) => ReactNode;
  onNewPointClicked?: (params: VisualModelNewPointClickedParams) => void;
  onPointClicked?: (params: VisualModelPointClickedParams<TPoint>) => void;
  onPointMoved?: (params: VisualModelSvgPointMovedParams<TPoint>) => void;
}

const _logPrefix = "VehicleVisualModelSvgViewer";

/** Viewer/editor for single visual model SVG image. */
export function VisualModelSvgViewer<TPoint extends VisualModelPoint>({
  visualModelId,
  visualModel,
  imageSelector,
  fallbackImageSelector,
  points,
  isFetchVisualModel = true,
  withVisualModelInfo = true,
  debugKey,
  svgViewerProps,
  renderCustomControls,
  onNewPointClicked,
  onPointClicked,
  onPointMoved,
}: VisualModelSvgViewerProps<TPoint>) {
  const [isVisualModelViewModalOpen, setIsVisualModelViewModalOpen] = useState(false);

  const visualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: visualModelId || "",
    },
    {
      deps: [visualModelId],
      skip: !isFetchVisualModel || !visualModelId || !!visualModel,
    },
  );
  const visualModelComputed = visualModelRequest.data || visualModel;

  const imageComputed = useMemo(
    () =>
      VisualModelHelper.getImageBySelector(visualModelComputed, imageSelector) ||
      VisualModelHelper.getImageBySelector(visualModelComputed, fallbackImageSelector),
    [
      visualModelComputed,
      ...VisualModelHelper.getImageSelectorsDeps([imageSelector, fallbackImageSelector]),
    ],
  );

  const pointsComputed = useMemo(
    () => VisualModelHelper.getVisualModelPointsForImage(imageComputed, points),
    [points, imageComputed],
  );

  const pointsByIdMap = useMemo(
    () =>
      chain(pointsComputed)
        .keyBy((x) => x.id)
        .mapValues((x) => x)
        .value(),
    [pointsComputed],
  );

  return (
    <Box>
      <GenericSvgViewer
        defaultPointStyle={VisualModelHelper.defaultSvgPointStyle}
        isZoomEnabled
        isResizeEnabled
        isPointDragEnabled={false}
        {...svgViewerProps}
        svgUrl={imageComputed?.file?.url}
        points={pointsComputed || []}
        svgProps={{
          height: { xxs: 200, sm: 300, md: 400, lg: 500 },
          width: { xxs: 200, sm: 300, md: 400, lg: 500 },
          minWidth: { xxs: 100, sm: 300, md: 300, lg: 500 },
          minHeight: { xxs: 100, sm: 300, md: 300, lg: 500 },
          maxWidth: { xxs: 200, sm: 300, md: 700, lg: 1000 },
          maxHeight: { xxs: 200, sm: 300, md: 700, lg: 700 },
          ...svgViewerProps.svgProps,
        }}
        isLoading={svgViewerProps?.isLoading || visualModelRequest.isLoading} // || !imageComputed
        debugKey={debugKey}
        customizations={{
          topRightBlock: withVisualModelInfo && !!visualModelComputed && (
            <Box>
              {/* Visual model info popover */}
              <AppPopover
                // clickBehavior={{}}
                hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                trigger={
                  <AppIconButton {...controlIconButtonProps}>
                    <AppIcon of='info' />
                  </AppIconButton>
                }
              >
                <AppPopoverContent isUseDefaultStyles={false} isTransparent>
                  <Alert severity='info' sx={{ maxWidth: { xxs: "90vw", md: 400 } }}>
                    <Box>Visual model:</Box>
                    <Box>
                      <VehicleVisualModelLink
                        entity={visualModelComputed}
                        entityId={visualModelComputed?.id}
                      />
                    </Box>
                    <AppButton
                      sx={{ mt: 1 }}
                      variant='outlined'
                      size='small'
                      color='text'
                      startIcon={<AppIcon of='quickView' />}
                      onClick={() => {
                        setIsVisualModelViewModalOpen(true);
                      }}
                    >
                      Quick view
                    </AppButton>
                  </Alert>
                </AppPopoverContent>
              </AppPopover>
            </Box>
          ),
          bottomRightBlock: renderCustomControls && (
            <Box>
              {renderCustomControls({
                iconButtonProps: controlIconButtonProps,
              })}
            </Box>
          ),
        }}
        onNewPointClicked={(params) => {
          console.log(_logPrefix, "onNewPointClicked:", { params });

          if (!params.isPointInsideViewBox) {
            return;
          }

          // handle click on <path /> only as only they represent vehicle part types
          const el = params.e.target as Element;
          if (!SvgHelper.isPathElement(el)) {
            return;
          }

          // allow clicks on <path /> without metadata
          const svgPathMetadata = VisualModelHelper.getMetadataFromSvgPathElement(el);
          // if (!metadata) {
          //   return;
          // }

          const params2: VisualModelNewPointClickedParams = {
            coords: params.fullCoords,
            svgPathMetadata: svgPathMetadata,
            area: svgPathMetadata?.vehicleArea || imageComputed?.area,
            projection: svgPathMetadata?.vehicleProjection || imageComputed?.projection,
          };
          onNewPointClicked && onNewPointClicked(params2);
        }}
        onPointClicked={(params) => {
          const point = pointsByIdMap[params.point.id];
          console.log(_logPrefix, "onPointClicked:", { params, point });
          if (!point) {
            return;
          }
          onPointClicked && onPointClicked({ point: point });
        }}
        onPointMoved={(params) => {
          console.log(_logPrefix, "onPointMoved:", { params });

          const point = pointsByIdMap[params.point.id];
          if (!point) {
            return;
          }

          // check all <path /> under the cursor for metadata
          const pathElsUnderCursor = document
            .elementsFromPoint(params.endE.clientX, params.endE.clientY)
            .filter((x) => SvgHelper.isPathElement(x));
          const newSvgPathMetadata = pathElsUnderCursor
            .map((x) => VisualModelHelper.getMetadataFromSvgPathElement(x)!)
            .filter(Boolean)
            .at(0);

          // console.log(_logPrefix, "1111:", { pathElsUnderCursor, newSvgPathMetadata });

          onPointMoved &&
            onPointMoved({
              ...params,
              point,
              startCoords: params.startCoords,
              endCoords: params.endCoords,
              svgPathMetadata: newSvgPathMetadata,
            });
        }}
      />

      {/* Visual model view */}
      {isVisualModelViewModalOpen && !!visualModelComputed && (
        <VehicleVisualModelViewModal
          open={isVisualModelViewModalOpen}
          onClose={() => {
            setIsVisualModelViewModalOpen(false);
          }}
          viewProps={{
            vehicleVisualModelId: visualModelComputed.id,
            vehicleVisualModel: visualModelComputed,
            onDelete: null,
          }}
        />
      )}
    </Box>
  );
}
